import React, { useState, useRef, useEffect } from "react";
import { MessageCircle, X, ChevronDown, Send, Loader2, ChevronRight } from "lucide-react";
import { invokeAiCompletion } from "../utils/ai";
import type { SecretaryNotification } from "../types";

/** Nom de l'assistant : identité premium du bot / mini secrétaire */
export const CRM_BOT_NAME = "Luma";

/** Robot ultra luxe : une seule orbe fluide, visage minimal, zéro empilement de formes */
function LumaIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 40 40"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden
    >
      <defs>
        <linearGradient id="luma-glow" x1="2" y1="2" x2="38" y2="38" gradientUnits="userSpaceOnUse">
          <stop stopColor="currentColor" stopOpacity="0.1" />
          <stop offset="1" stopColor="currentColor" stopOpacity="0.03" />
        </linearGradient>
        <radialGradient id="luma-orb" cx="50%" cy="42%" r="52%" gradientUnits="objectBoundingBox">
          <stop stopColor="currentColor" stopOpacity="0.88" />
          <stop offset="0.6" stopColor="currentColor" stopOpacity="0.72" />
          <stop offset="1" stopColor="currentColor" stopOpacity="0.5" />
        </radialGradient>
        <radialGradient id="luma-eye" cx="50%" cy="40%" r="60%" gradientUnits="objectBoundingBox">
          <stop stopColor="currentColor" stopOpacity="0.55" />
          <stop offset="1" stopColor="currentColor" stopOpacity="0.1" />
        </radialGradient>
      </defs>
      {/* Halo très discret */}
      <circle cx="20" cy="20" r="17" fill="url(#luma-glow)" />
      {/* Une seule forme : orbe (ellipse), rien d'autre */}
      <ellipse cx="20" cy="20" rx="13" ry="14" fill="url(#luma-orb)" />
      {/* Visage minimal sur l'orbe */}
      <ellipse cx="15" cy="18" rx="2" ry="2.2" fill="url(#luma-eye)" />
      <ellipse cx="25" cy="18" rx="2" ry="2.2" fill="url(#luma-eye)" />
      <circle cx="15.2" cy="17.2" r="0.4" fill="currentColor" fillOpacity="0.4" />
      <circle cx="25.2" cy="17.2" r="0.4" fill="currentColor" fillOpacity="0.4" />
      <path
        d="M15.5 22.2c0 0 2.2 1.2 4.5 1.2s4.5-1.2 4.5-1.2"
        stroke="currentColor"
        strokeOpacity="0.28"
        strokeWidth="0.85"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}

const CRM_CONTEXT = `Tu es Luma, l'assistant expert du CRM installateur multi-services. Tu es une mini secrétaire premium et bienveillante. Ton rôle : aider les utilisateurs à comprendre et utiliser le CRM (dossiers chantiers, partenaires, poseurs, stock, planning).

## Connaissance du CRM
- **Rôles** : Admin (tableau de bord, Dossiers, Stock, Accès partenaires/poseurs, Équipe, Réglages, abonnement), Partenaire (créer/voir ses dossiers, zone de stock), Poseur (chantiers assignés, validation avec consommation stock, notes de pose).
- **Dossiers** : client (nom, tél, adresse), surface, type d'éclairage, hauteur sous plafond, estimation LED, notes. Statuts : En attente → Planifié (date/heure) → Terminé ou Annulé. Assignation partenaire + poseur, préparation stock par référence, région de stock.
- **Stock** : références avec quantités et seuil d'alerte, régions de stock. Préparation par dossier avant la pose ; à la validation le stock est déduit.
- **Planning** : calendrier des chantiers planifiés.
- **Accès** : onglet pour gérer partenaires et poseurs (nom, code de connexion, zone par défaut). Ils se connectent avec leur code.
- **Équipe** : invitations par email (owner/admin/member), permissions par onglet.
- **Logs** : historique des actions. **Facturation partenaire** : neuf / remplacement par référence et région.

## Règles de réponse
- Réponds **toujours en français**, de façon **courte, claire et actionnable**.
- Donne des **étapes concrètes** quand c'est une question "comment faire" (ex : "1. Allez dans… 2. Cliquez sur…").
- Utilise les **vrais noms d'onglets et de champs** du CRM (Dossiers, Stock, Accès, Planning, Réglages, etc.).
- Ton : **expert, bienveillant, premium** — une phrase d'intro si utile, puis l'essentiel. Évite le blabla.
- Si la question sort du cadre du CRM, réponds en une phrase : "Je suis Luma, ton assistante. Je peux t'aider sur le CRM : dossiers, stock, planning, accès. Pose-moi une question sur ces sujets."
- Pas de listes à rallonge sauf si vraiment nécessaire ; privilégie 3–5 points max.`;

const QUICK_TIPS = [
  { title: "Dossiers", text: "Créez des dossiers par chantier, assignez un partenaire et un poseur, suivez le statut (en attente → planifié → terminé)." },
  { title: "Stock", text: "Définissez des références et des régions de stock. Préparez le matériel par dossier avant la pose." },
  { title: "Planning", text: "Consultez le calendrier pour voir les chantiers planifiés et les disponibilités." },
  { title: "Accès", text: "Partenaires et poseurs se connectent avec un code unique ; vous gérez leurs accès depuis l’onglet Accès." },
];

export interface CRMHelpBotProps {
  /** Notifications mini secrétaire (admin uniquement, quand activée) */
  notifications?: SecretaryNotification[];
  /** Callback pour naviguer vers un onglet du dashboard (admin) */
  onNavigateToTab?: (tab: string, payload?: { regionId?: string; partnerId?: string }) => void;
}

export function CRMHelpBot({ notifications = [], onNavigateToTab }: CRMHelpBotProps) {
  const [open, setOpen] = useState(false);
  const [hiddenForPage, setHiddenForPage] = useState(false);
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const answerEndRef = useRef<HTMLDivElement>(null);
  /** Notif affichée dans le panneau quand ouverture depuis la bulle */
  const [selectedNotification, setSelectedNotification] = useState<SecretaryNotification | null>(null);
  /** True dès que l'admin a ouvert le bot et "lu" les notifs (bulle + badge disparaissent) */
  const [notificationsReadThisSession, setNotificationsReadThisSession] = useState(false);

  const showBubble = notifications.length > 0 && !notificationsReadThisSession;
  const showBadge = notifications.length > 0 && !notificationsReadThisSession;

  useEffect(() => {
    if (open && answer) {
      answerEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [open, answer]);

  useEffect(() => {
    if (open) {
      inputRef.current?.focus();
    } else {
      setQuestion("");
      setAnswer(null);
      setError(null);
      setSelectedNotification(null);
    }
  }, [open]);

  const handleBubbleClick = (notif: SecretaryNotification) => {
    setNotificationsReadThisSession(true);
    setSelectedNotification(notif);
    setOpen(true);
  };

  const handleOpenBot = () => {
    if (notifications.length > 0) setNotificationsReadThisSession(true);
    setHiddenForPage(false);
    setOpen(true);
  };

  const handleNotificationAction = (notif: SecretaryNotification) => {
    onNavigateToTab?.(notif.action.tab, {
      regionId: notif.action.regionId,
      partnerId: notif.action.partnerId,
    });
    setOpen(false);
  };

  const handleAsk = async () => {
    const q = question.trim();
    if (!q || loading) return;
    setLoading(true);
    setError(null);
    setAnswer(null);
    try {
      const { text } = await invokeAiCompletion({
        prompt: `Question de l'utilisateur sur le CRM : "${q}"\n\nRéponds de façon structurée et actionnable, avec les vrais noms des écrans et boutons du CRM.`,
        systemInstruction: CRM_CONTEXT,
        maxOutputTokens: 768,
        temperature: 0.4,
      });
      setAnswer(text);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur lors de la réponse.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {hiddenForPage && !open && (
        <button
          type="button"
          onClick={() => setHiddenForPage(false)}
          className="fixed bottom-6 right-6 z-[900] rounded-full bg-white/90 dark:bg-slate-800/90 border border-gray-200/60 dark:border-white/10 px-3 py-2 text-xs font-medium text-gray-700 dark:text-slate-200 shadow"
        >
          Afficher Luma
        </button>
      )}
      {/* Bulle mini secrétaire (une fois par session, au-dessus du bouton) */}
      {showBubble && !hiddenForPage && (
        <button
          type="button"
          onClick={() => handleBubbleClick(notifications[0])}
          className="fixed bottom-20 right-6 z-[899] max-w-[280px] sm:max-w-[320px] text-left rounded-2xl bg-white dark:bg-slate-800 shadow-[0_8px_32px_rgba(0,0,0,0.12),0_0_0_1px_rgba(0,0,0,0.06)] dark:shadow-[0_8px_32px_rgba(0,0,0,0.4),0_0_0_1px_rgba(255,255,255,0.06)] backdrop-blur-xl border border-gray-200/60 dark:border-white/10 p-4 transition-all duration-300 hover:scale-[1.02] hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:ring-offset-2 focus:ring-offset-slate-100 dark:focus:ring-offset-slate-900"
          aria-label={`${CRM_BOT_NAME} – Voir le résumé`}
        >
          <p className="text-[14px] font-medium text-gray-900 dark:text-white leading-snug">
            {notifications.length === 1
              ? notifications[0].shortMessage
              : `Vous avez ${notifications.length} rappels`}
          </p>
          <p className="text-[12px] text-gray-500 dark:text-slate-400 mt-1">
            {CRM_BOT_NAME} · Cliquez pour en savoir plus
          </p>
        </button>
      )}

      {/* Bouton Luma flottant */}
      <button
        type="button"
        onClick={handleOpenBot}
        style={{ display: hiddenForPage ? "none" : undefined }}
        className="fixed bottom-6 right-6 z-[900] flex h-14 w-14 items-center justify-center rounded-full bg-white/90 dark:bg-slate-800/90 shadow-[0_8px_32px_rgba(0,0,0,0.12),0_0_0_1px_rgba(0,0,0,0.06)] dark:shadow-[0_8px_32px_rgba(0,0,0,0.4),0_0_0_1px_rgba(255,255,255,0.06)] backdrop-blur-xl border border-gray-200/60 dark:border-white/10 transition-all duration-300 hover:scale-105 hover:shadow-[0_12px_40px_rgba(0,0,0,0.15)] dark:hover:shadow-[0_12px_40px_rgba(0,0,0,0.5)] focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:ring-offset-2 focus:ring-offset-slate-100 dark:focus:ring-offset-slate-900"
        aria-label={`${CRM_BOT_NAME} – Aide CRM`}
        title={CRM_BOT_NAME}
      >
        <span className="relative flex h-9 w-9 items-center justify-center text-slate-600 dark:text-slate-300">
          <LumaIcon className="h-9 w-9" />
          {showBadge && (
            <span className="absolute -right-0.5 -top-0.5 flex h-2.5 w-2.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-400/60 opacity-75" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-amber-500" />
            </span>
          )}
        </span>
        {!open && (
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              setHiddenForPage(true);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.stopPropagation();
                setHiddenForPage(true);
              }
            }}
            className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-white dark:bg-slate-900 border border-gray-200 dark:border-white/10 text-gray-500 dark:text-slate-300 text-[10px] leading-none flex items-center justify-center hover:text-gray-900 dark:hover:text-white"
            aria-label="Masquer l'assistante pour cette page"
          >
            ×
          </span>
        )}
      </button>

      {/* Panneau aide */}
      {open && (
        <div
          className="fixed inset-0 z-[950] flex items-end justify-end sm:items-center sm:justify-end p-0 sm:p-4"
          role="dialog"
          aria-label={`${CRM_BOT_NAME} – Aide CRM`}
        >
          <div
            className="absolute inset-0 bg-black/20 dark:bg-black/40 backdrop-blur-[2px] sm:backdrop-blur-sm transition-opacity duration-300"
            onClick={() => setOpen(false)}
            aria-hidden
          />
          <div
            className="relative w-full sm:max-w-[400px] h-[85vh] sm:h-[520px] sm:max-h-[85vh] flex flex-col rounded-t-3xl sm:rounded-3xl bg-white/95 dark:bg-slate-900/95 backdrop-blur-2xl border border-gray-200/80 dark:border-white/10 shadow-[0_-8px_40px_-12px_rgba(0,0,0,0.15),0_0_0_1px_rgba(0,0,0,0.05)] dark:shadow-[0_-8px_40px_-12px_rgba(0,0,0,0.4),0_0_0_1px_rgba(255,255,255,0.06)] animate-in slide-in-from-bottom duration-300 sm:zoom-in-95 sm:slide-in-from-right-4"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between shrink-0 px-5 py-4 border-b border-gray-100 dark:border-white/10">
              <div className="flex items-center gap-2.5">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-slate-100 to-slate-50 dark:from-white/10 dark:to-white/5 border border-gray-200/60 dark:border-white/10 text-slate-600 dark:text-slate-300">
                  <LumaIcon className="h-6 w-6" />
                </div>
                <div>
                  <h2 className="text-[17px] font-semibold tracking-tight text-gray-900 dark:text-white">
                    {CRM_BOT_NAME}
                  </h2>
                  <p className="text-[13px] text-gray-500 dark:text-slate-400">
                    Votre assistante CRM
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="flex h-9 w-9 items-center justify-center rounded-xl text-gray-400 dark:text-slate-500 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/10 transition-colors duration-200"
                aria-label="Fermer"
              >
                <X className="h-5 w-5" strokeWidth={1.75} />
              </button>
            </div>

            {/* Contenu scrollable */}
            <div className="flex-1 overflow-y-auto overscroll-contain px-5 py-4 space-y-5">
              {selectedNotification ? (
                <>
                  <div className="rounded-xl bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/10 p-3.5">
                    <p className="text-[14px] text-gray-700 dark:text-slate-300 leading-relaxed whitespace-pre-wrap">
                      {selectedNotification.fullMessage}
                    </p>
                  </div>
                  {onNavigateToTab && (
                    <button
                      type="button"
                      onClick={() => handleNotificationAction(selectedNotification)}
                      className="w-full flex items-center justify-center gap-2 rounded-xl bg-blue-500 text-white px-4 py-3 text-[14px] font-medium shadow-lg shadow-blue-500/25 hover:bg-blue-600 transition-colors"
                    >
                      {selectedNotification.action.label}
                      <ChevronRight className="h-4 w-4" strokeWidth={2} />
                    </button>
                  )}
                </>
              ) : (
                <>
                  <div>
                    <h3 className="text-[11px] font-medium uppercase tracking-wider text-gray-400 dark:text-slate-500 mb-2">
                      En bref
                    </h3>
                    <ul className="space-y-2">
                      {QUICK_TIPS.map((tip) => (
                        <li key={tip.title} className="group">
                          <span className="text-[13px] font-medium text-gray-900 dark:text-white">
                            {tip.title}
                          </span>
                          <p className="text-[13px] text-gray-600 dark:text-slate-400 mt-0.5 leading-relaxed">
                            {tip.text}
                          </p>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-[11px] font-medium uppercase tracking-wider text-gray-400 dark:text-slate-500 mb-2 flex items-center gap-1.5">
                      <MessageCircle className="h-3.5 w-3.5" />
                      Une question ?
                    </h3>
                <div className="flex gap-2">
                  <input
                    ref={inputRef}
                    type="text"
                    value={question}
                    onChange={(e) => setQuestion(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleAsk()}
                    placeholder="Ex. Comment ajouter un partenaire ?"
                    className="flex-1 min-w-0 rounded-xl border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 px-4 py-2.5 text-[14px] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500/50 transition-shadow"
                  />
                  <button
                    type="button"
                    onClick={handleAsk}
                    disabled={loading || !question.trim()}
                    className="shrink-0 flex h-10 w-10 items-center justify-center rounded-xl bg-blue-500 text-white shadow-lg shadow-blue-500/25 hover:bg-blue-600 disabled:opacity-50 disabled:pointer-events-none transition-colors"
                    aria-label="Envoyer"
                  >
                    {loading ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      <Send className="h-5 w-5" strokeWidth={1.75} />
                    )}
                  </button>
                </div>
                {error && (
                  <p className="mt-2 text-[13px] text-red-500 dark:text-red-400">{error}</p>
                )}
                {answer && (
                  <div className="mt-3 rounded-xl bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/10 p-3.5">
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <ChevronDown className="h-4 w-4 text-blue-500 rotate-[-90deg]" />
                      <span className="text-[11px] font-medium uppercase tracking-wider text-blue-600 dark:text-blue-400">
                        Réponse
                      </span>
                    </div>
                    <p className="text-[14px] text-gray-700 dark:text-slate-300 leading-relaxed whitespace-pre-wrap">
                      {answer}
                    </p>
                    <div ref={answerEndRef} />
                  </div>
                )}
                  </div>
                </>
              )}
            </div>

            {/* Safe area mobile */}
            <div className="shrink-0 h-[env(safe-area-inset-bottom,0px)]" />
          </div>
        </div>
      )}
    </>
  );
}
